import React, {PureComponent, ReactNode} from 'react'
import MeetingComment, {CommentAttachment} from '../model/MeetingComment'
import {getLinkIconComponent, IconBox, MeetingCommentTypes, TrashIcon} from '../AssocDirectorCommon'
import { Box, Drawer, Menu, MenuItem, Popover } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {Colors, FontWeights} from '../../../styledComponents/Constants'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import DeletedComment from '../model/DeletedComment'
import {Nullable} from '../../../app/TypeHelpers'
import styled from 'styled-components'
import {StyledHR} from '../../../styledComponents/StyledTypeDefinitions'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faDownload, faEllipsisH, faPen, faTrash} from '@fortawesome/free-solid-svg-icons'
import MeetingCommentReply from '../model/MeetingCommentReply'
import {getFileExtension} from '../../../__NoAutomatedTests/common/util/Natutil'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import {StyledPre} from '../../../styledComponents/adminPage/StyledPre'
import {UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import MeetingCommentView from '../model/MeetingCommentView'
import DeletedCommentView from '../model/DeletedCommentView'
import {InputCommentContent} from './AdminMeetingCommentArea'
import {MeetingHelper} from '../utils/MeetingHelper'
import {isMobile} from 'react-device-detect'
import {
    CenterRowFlexContainer,
    FlexContainer,
    FullWidthLeftRowFlexContainer,
    LeftRowFlexContainer,
    RowFlexContainer,
} from '../../../styledComponents/FlexComponents'
import {NoWrapSpan} from '../shared/AssocComponents'

interface Props {
    condominiumCd: string
    meetingId: number
    meetingComment: MeetingCommentTypes
    sourceMeetingComment: MeetingCommentView | DeletedCommentView
    onClickReaction: (comment: MeetingCommentTypes, isReaction: boolean) => Promise<Nullable<void>>
    onClickCommentDelete: (comment: MeetingCommentTypes) => void
    onClickEditButton: (
        comment: MeetingComment | MeetingCommentReply,
    ) => void
    editCommentContent?: InputCommentContent
    renderCommentEditArea: (content: InputCommentContent) => ReactNode
    renderAdditionalAction?: (comment: MeetingCommentView | DeletedCommentView) => ReactNode
    eventEmitter: EventEmitterExAdmin
    havePostCommentAuthority: boolean
    verticalDivider?: boolean
    currentDatetime: string
}

interface State {
    isOpenReactionArea: boolean
    isClosingReactionArea: boolean
    isOpenCommentMenu: boolean
    reactionButton: Nullable<HTMLElement>
    commentMenuButton: Nullable<HTMLElement>
}

class AdminMeetingCommentComponent extends PureComponent<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            isOpenReactionArea: false,
            isClosingReactionArea: false,
            isOpenCommentMenu: false,
            reactionButton: null,
            commentMenuButton: null,
        }
    }

    handleClickReactionOpenButton = (event: React.MouseEvent<HTMLElement>): void => {
        this.setState({
            isOpenReactionArea: true,
            reactionButton: event.currentTarget,
        })
    }

    handleCloseReactionArea = (): void => {
        this.closeReactionArea(true)
    }

    closeReactionArea(clearHeadingAreaMessage: boolean): void {
        this.setState({
            isOpenReactionArea: false,
            reactionButton: null,
        })

        if (clearHeadingAreaMessage) {
            this.props.eventEmitter.clearHeadingAreaMessage()
        }
    }

    handleClickReactionButtonForPopover = (isReaction: boolean) => async (): Promise<void> => {
        if (this.state.isClosingReactionArea) {
            return
        }

        const {onClickReaction, meetingComment} = this.props
        const result = await onClickReaction(meetingComment, isReaction)
        if (result === null) {
            this.closeReactionArea(false)
            this.setState({
                isClosingReactionArea: false,
            })
            return
        }
        this.setState({
            isClosingReactionArea: true,
        }, () => {
            setTimeout(() => {
                this.closeReactionArea(true)
                this.setState({
                    isClosingReactionArea: false,
                })
            }, 500)
        })
    }

    handleClickCommentMenuButton = (event: React.MouseEvent<HTMLElement>): void => {
        this.setState({
            isOpenCommentMenu: true,
            commentMenuButton: event.currentTarget,
        })
    }

    handleCloseCommentMenu = (): void => {
        this.closeCommentMenu()
    }

    closeCommentMenu(): void {
        this.setState({
            isOpenCommentMenu: false,
            commentMenuButton: null,
        })

        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    handleClickCommentDeleteButton = (): void => {
        this.closeCommentMenu()
        this.props.eventEmitter.showDialog({
                title: 'コメントを削除',
                errorLevel: MESSAGE_TYPE.INFORMATION,
                message: <>
                    このコメントを削除してよろしいですか？<br/>
                    削除したコメントは元に戻せません。
                </>,
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleClickDeleteCancelButton,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleClickDeleteOkButton,
                    },
                ],
            },
        )
    }

    handleClickDeleteCancelButton = (): void => {
        this.props.eventEmitter.closeDialog()
    }

    handleClickDeleteOkButton = (): void => {
        const {onClickCommentDelete, meetingComment} = this.props
        this.props.eventEmitter.closeDialog()
        onClickCommentDelete(meetingComment)
    }

    handleClickCommentEditButton = (): void => {
        const {meetingComment, onClickEditButton} = this.props
        if (meetingComment instanceof MeetingComment) {
            onClickEditButton(
                meetingComment,
            )
        }
        this.closeCommentMenu()
    }

    renderAttachment(attachmentList: CommentAttachment[]): ReactNode {
        return attachmentList.map((attachment, index) => {
            return <AttachmentFile key={`attachment-${index}`}>
                <StyledLink to={attachment.uri} color={'inherit'}>
                    <FullWidthLeftRowFlexContainer>
                        <IconBox>
                            {getLinkIconComponent(getFileExtension(attachment.displayName))}
                        </IconBox>
                        <AttachmentFileName>{attachment.displayName}</AttachmentFileName>
                        <IconBox>
                            <FontAwesomeIcon icon={faDownload}/>
                        </IconBox>
                    </FullWidthLeftRowFlexContainer>
                </StyledLink>
            </AttachmentFile>
        })
    }

    renderCommentHeader(comment: MeetingCommentTypes): ReactNode {
        if (comment instanceof DeletedComment) {
            return (<>
                <RowFlexContainer>
                    <LeftRowFlexContainer>
                        <TrashIcon/>
                        <Box ml={'15px'}>
                            <AdminMiddleFontDiv>{comment.message}</AdminMiddleFontDiv>
                        </Box>
                    </LeftRowFlexContainer>
                </RowFlexContainer>
            </>)
        }
        const userIconNode: ReactNode = MeetingHelper.getUserIconNodeByResidentStatus(
            comment.user.isWithinTerm,
            comment.user.hasMailAddress,
            comment.user.role,
            comment.user.roomList,
            'large')

        return <RowFlexContainer>
            <LeftRowFlexContainer>
                {userIconNode}
                <Box ml={'15px'}>
                    <UserNameAndRoomDisplayArea>
                        <NoWrapSpan>
                            <UserNameDiv>
                                {MeetingHelper.createCommentUserNameByResidentStatus(comment.user)}
                            </UserNameDiv>
                        </NoWrapSpan>
                        <Box width={'4px'}/>
                        <NoWrapSpan>
                            <RoomNumberText>
                                {MeetingHelper.getDisplayRoomListByResidentStatus(comment.user.roomList)}
                            </RoomNumberText>
                        </NoWrapSpan>
                    </UserNameAndRoomDisplayArea>
                    <CommentDatetime>
                        {MeetingHelper.formatDoNotDisplayCurrentYear(
                            this.props.currentDatetime, comment.updateDatetime,
                        )} {comment.version > 1 && '編集済み'}
                    </CommentDatetime>
                </Box>
            </LeftRowFlexContainer>
            {
                this.props.havePostCommentAuthority && comment.isYou && !this.props.editCommentContent &&
                <div>
                    <StyledLink
                        id={'commentMenuButton-' + comment.meetingCommentId}
                        onClick={this.handleClickCommentMenuButton}
                    >
                        <FontAwesomeIcon icon={faEllipsisH} color={Colors.AccentDark}/>
                    </StyledLink>
                    <Menu
                        id={'pp-meeting-comment-menu'}
                        open={this.state.isOpenCommentMenu}
                        onClose={this.handleCloseCommentMenu}
                        anchorEl={this.state.commentMenuButton}
                    >
                        <MenuItem onClick={this.handleClickCommentEditButton}>
                            <LeftRowFlexContainer>
                                <FontAwesomeIcon icon={faPen}/>
                                <Box width={'15px'}/>
                                コメントを編集
                            </LeftRowFlexContainer>
                        </MenuItem>
                        <MenuItem onClick={this.handleClickCommentDeleteButton}>
                            <LeftRowFlexContainer>
                                <FontAwesomeIcon icon={faTrash}/>
                                <Box width={'15px'}/>
                                コメントを削除
                            </LeftRowFlexContainer>
                        </MenuItem>
                    </Menu>
                </div>
            }
        </RowFlexContainer>
    }

    renderCommentBody(comment: MeetingCommentTypes): ReactNode {
        if (comment instanceof DeletedComment) {
            return
        }

        const commentReactedUserList = comment.commentReactedUserList
        const isYouReaction = commentReactedUserList.some(commentReactedUser => commentReactedUser.isYou)
        const reactionCount = comment.commentReactedUserList.length

        const reactionListNode = <>
            <RowFlexContainer>
                <ReactionTitle>見ましたを押した人({reactionCount})</ReactionTitle>
                <UnstyledLinkStyledButton
                    onClick={this.handleClickReactionButtonForPopover(!isYouReaction)}
                >
                    <img
                        src={isYouReaction ? '/resources/images/icons/icon_mimashita.svg' : '/resources/images/icons/icon_mimashita_dotted.svg'}
                        width={'40px'}
                        alt={'見ました'}
                    />
                </UnstyledLinkStyledButton>
            </RowFlexContainer>
            <Box height={'12px'}/>
            <ReactionListContainer>
                {
                    commentReactedUserList.map((user, index) => {
                        let displayName = MeetingHelper.createCommentUserNameByResidentStatus({
                            familyName: user.familyName,
                            firstName: user.firstName,
                            role: user.role,
                            roomList: user.roomList,
                            isWithinTerm: user.isWithinTerm,
                            hasMailAddress: user.hasMailAddress,
                        })
                        if (user.isYou) {
                            displayName += ' (あなた)'
                        }
                        const userIconNode: ReactNode = MeetingHelper.getUserIconNodeByResidentStatus(
                            user.isWithinTerm,
                            user.hasMailAddress,
                            user.role,
                            user.roomList,
                            'medium')
                        return <Box key={'commentReactedUser-' + index} mt={'2px'} minHeight={'28px'}>
                            <LeftRowFlexContainer>
                                <CommentReactedUserIconBox>
                                    {userIconNode}
                                </CommentReactedUserIconBox>
                                <UserNameAndRoomDisplayArea>
                                    <NoWrapSpan>
                                        <AdminMiddleFontDiv>{displayName}</AdminMiddleFontDiv>
                                    </NoWrapSpan>
                                    <Box width={'4px'}/>
                                    <NoWrapSpan>
                                        <RoomNumberText>
                                            {MeetingHelper.getDisplayRoomListByResidentStatus(user.roomList)}
                                        </RoomNumberText>
                                    </NoWrapSpan>
                                </UserNameAndRoomDisplayArea>
                            </LeftRowFlexContainer>
                        </Box>
                    })
                }
            </ReactionListContainer>
        </>

        return <>
            <Box height={'5px'}/>
            {
                this.props.editCommentContent ?
                    this.props.renderCommentEditArea(this.props.editCommentContent)
                    :
                    <>
                        <StyledPre>{comment.content}</StyledPre>
                        {this.renderAttachment(comment.attachmentList)}
                    </>
            }
            {isMobile ?
                <ReactionDrawer
                    anchor={'bottom'}
                    open={this.state.isOpenReactionArea}
                    onClose={this.handleCloseReactionArea}
                    SlideProps={{onExited: this.handleCloseReactionArea}}
                >
                    <Box m={'15px 15px 0px 15px'}>
                        {reactionListNode}
                    </Box>
                </ReactionDrawer>
                :
                <ReactionPopover
                    open={this.state.isOpenReactionArea}
                    onClose={this.handleCloseReactionArea}
                    anchorEl={this.state.reactionButton}
                    anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                >
                    <Box p={'10px 10px 10px 15px'}>
                        {reactionListNode}
                    </Box>
                </ReactionPopover>
            }
        </>
    }

    renderActionArea(comment: MeetingCommentTypes): ReactNode {
        if (comment instanceof DeletedComment) {
            return <>
                <Box m={'5px'}/>
                {
                    this.props.renderAdditionalAction ?
                        this.props.renderAdditionalAction(this.props.sourceMeetingComment)
                        :
                        <Box height={'5px'}/>
                }
                <Box height={'2px'}/>
            </>
        }
        const commentReactedUserList = comment.commentReactedUserList
        const isYouReaction = commentReactedUserList.some(commentReactedUser => commentReactedUser.isYou)
        const reactionCount = comment.commentReactedUserList.length

        return <>
            <Box height={'5px'}/>
            <RowReverseFlexContainer>
                <StyledLink
                    onClick={this.handleClickReactionOpenButton}
                >
                    <LeftRowFlexContainer>
                        <ReactionIcon>
                            <img
                                src={isYouReaction ? '/resources/images/icons/icon_mimashita.svg' : '/resources/images/icons/icon_mimashita_grey.svg'}
                                width={'25px'}
                                alt={'見ました'}
                            />
                        </ReactionIcon>
                        <ReactionLabel>
                            <RowFlexContainer>
                                <Box minWidth={'3px'}>{reactionCount > 0 ? reactionCount : ''}</Box>
                            </RowFlexContainer>
                        </ReactionLabel>
                    </LeftRowFlexContainer>
                </StyledLink>
                {
                    this.props.renderAdditionalAction &&
                    this.props.renderAdditionalAction(this.props.sourceMeetingComment)
                }
            </RowReverseFlexContainer>
        </>
    }

    render(): ReactNode {
        const {meetingComment, verticalDivider, renderAdditionalAction} = this.props
        const shiftActionArea = verticalDivider && renderAdditionalAction !== undefined

        return (
            <MeetingCommentArea>
                {this.renderCommentHeader(meetingComment)}
                <StretchRowFlexContainer>
                    <VerticalDividerContainer>
                        {
                            verticalDivider &&
                            <VerticalDivider/>
                        }
                    </VerticalDividerContainer>
                    <Box width={'13px'}/>
                    <ContentBodyBox>
                        {this.renderCommentBody(meetingComment)}
                        {
                            !shiftActionArea &&
                            this.renderActionArea(meetingComment)
                        }
                    </ContentBodyBox>
                </StretchRowFlexContainer>
                {
                    shiftActionArea &&
                    <Box ml={'13px'}>
                        {this.renderActionArea(meetingComment)}
                    </Box>
                }
            </MeetingCommentArea>
        )
    }
}

export default AdminMeetingCommentComponent

const MeetingCommentArea = styled.div`
    padding: 3px 15px 5px 10px;

    :hover {
        background-color: rgb(251, 251, 251);
    }
`

const StretchRowFlexContainer = styled(LeftRowFlexContainer)`
    align-items: stretch;
`

const VerticalDividerContainer = styled(CenterRowFlexContainer)`
    align-items: stretch;
    min-width: 32px;
`

const VerticalDivider: StyledHR = styled.hr`
    margin: 0;
    width: 3px;
    height: 100%;
    border: none;
    background-color: ${Colors.Background};
`

const ContentBodyBox = styled(Box)`
    flex-grow: 2;
    align-items: flex-end;
    overflow: hidden;
`

export const UserNameDiv = styled.div`
    font-weight: ${FontWeights.Bold};
    font-size: 14px;
`

const CommentDatetime = styled.div`
    font-size: 12px;
    color: ${Colors.AccentDark};
`

export const AttachmentFileName = styled.div`
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
`

const ReactionIcon = styled.div`
    display: flex;
    margin-right: 5px;
`

const ReactionLabel = styled.label`
    font-size: 12px;
    color: ${Colors.AccentDark};
`

export const AttachmentFile = styled.div`
    margin-top: 5px;
    padding: 5px 10px;
    background-color: ${Colors.Surface};
    border: 1px solid ${Colors.Background};
    border-radius: 4px;
    width: 100%;
    max-width: 480px;
`

const ReactionListContainer = styled.div`
    max-height: 300px;

    overflow: auto;
`

const ReactionTitle = styled.div`
    font-size: 16px;
    line-height: 18px;
`

const CommentReactedUserIconBox = styled(Box)`
    text-align: center;
    margin-right: 12px;
`

const ReactionPopover = withStyles({
    paper: {
        minWidth: '400px',
    },
})(Popover)

const ReactionDrawer = withStyles({
    paper: {
        border: `1px solid ${Colors.Background}`,
        borderRadius: '10px 10px 0 0',
        height: '200px',
        overflow: 'hidden',
    },
})(Drawer)

const RowReverseFlexContainer = styled(FlexContainer)`
    flex-direction: row-reverse;
`

const AdminMiddleFontDiv = styled.div`
    font-size: 14px;
`

const RoomNumberText = styled.span`
    white-space: normal;
    padding: 5px 16px 5px 0;
    font-size: 12px;
    color: ${Colors.AccentDark};
    width: 100%;
`
const UserNameAndRoomDisplayArea = styled(LeftRowFlexContainer)`
    -webkit-flex-wrap: wrap;
`
